import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import validator from 'validator';
import { Form } from 'Validation';
import HiddenFormFields from 'HiddenFormFields';
import NewAccountTermsOfService from 'NewAccountTermsOfService';
import BaseFormRow from 'CheckoutForm/components/FormRow/BaseFormRow';
import FormSwiftAPI from 'formswift-api';
import EmailFormField from './components/EmailFormField';
import PasswordFormField from './components/PasswordFormField';
import ConfirmPasswordFormField from './components/ConfirmPasswordFormField';
import RegisterFormTOSPositionEnum from './RegisterFormConstants';
import getEmailConsent from '../lib/getEmailConsent';

class RegisterForm extends React.Component {
  constructor(props) {
    super(props);
    const countryIsoAlpha2 = window.CurrentUser ? window.CurrentUser.countryIsoAlpha2 : '';
    const {
      defaultConsent: defaultMarketingConsent,
      isShown: isEmailMarketingConsentOptionShown,
    } = getEmailConsent(countryIsoAlpha2);

    this.state = {
      'valid-email': false,
      'validations-email': [validator.isEmail],
      confirmPassword: '',
      defaultMarketingConsent,
      hasSubmittedRegisterRequest: false,
      isConsentToEmailMarketingGiven: defaultMarketingConsent,
      isEmailMarketingConsentOptionShown,
    };

    this.validateField = this.validateField.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onPasswordChange = this.onPasswordChange.bind(this);
    this.onMarketingConsentChange = this.onMarketingConsentChange.bind(this);
    this.getHiddenFormFields = this.getHiddenFormFields.bind(this);
    this.getEmailFormField = this.getEmailFormField.bind(this);
    this.getPasswordFormField = this.getPasswordFormField.bind(this);
    this.getConfirmPasswordFormField = this.getConfirmPasswordFormField.bind(this);
    this.getPromoCodeFormField = this.getPromoCodeFormField.bind(this);
    this.getEmailConsentCheckbox = this.getEmailConsentCheckbox.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
  }

  onChange(event) {
    const validations = this.state[`validations-${event.target.dataset.statekey}`];
    const numOfValidations = validations.length;
    let valid = true;
    for (let i = 0; i < numOfValidations; i += 1) {
      valid = valid && validations[i](event.target.value);
    }

    this.setState({
      [`valid-${event.target.dataset.statekey}`]: valid,
    });
  }

  onPasswordChange(e) {
    if (!this.props.hideConfirmPassword) {
      return;
    }
    this.setState({
      confirmPassword: e.currentTarget.value,
    });
  }

  onMarketingConsentChange() {
    this.setState({ isConsentToEmailMarketingGiven: (
      this.state.defaultMarketingConsent === undefined &&
      this.state.isConsentToEmailMarketingGiven === undefined
      ? false
      : !this.state.isConsentToEmailMarketingGiven),
    });
  }

  getEmailFormField() {
    return (
      <EmailFormField
        emailPlaceholder={this.props.emailPlaceholder}
        onChange={this.onChange}
      />
    );
  }

  getHiddenFormFields() {
    const hiddenFields = [
      { name: 'funnelCategory', value: this.props.funnelCategory },
      { name: 'funnelPage', value: this.props.funnelPage },
      { name: 'primary_document_type', value: this.props.documentType },
      { name: 'session_id', value: this.props.sessionKey },
      { name: 'session_hash', value: this.props.sessionHash },
      { name: 'redirect_url', value: this.props.redirectToPage },
      { name: 'originalPage', value: this.props.originalPage },
      { name: 'fs-newsletter', value: this.props.newsletter },
      { name: 'fs-newsletter-alt', value: this.props.newsletter },
    ];

    if (this.props.hideConfirmPassword) {
      hiddenFields.push({ name: 'confirm_password', value: this.state.confirmPassword, id: 'confirm_password' });
    }

    return (
      <HiddenFormFields
        className="hidden-form-fields"
        fieldAttributesArr={hiddenFields}
      />
    );
  }

  getPasswordFormField() {
    const enablePassword = this.isFieldEnabled('password');
    return (
      <PasswordFormField
        enabled={enablePassword}
        passwordPlaceholder={this.props.passwordPlaceholder}
        onPasswordChange={this.onPasswordChange}
      />
    );
  }

  getConfirmPasswordFormField() {
    if (this.props.hideConfirmPassword) {
      return null;
    }
    const enablePassword = this.isFieldEnabled('password');
    return (
      <ConfirmPasswordFormField
        enabled={enablePassword}
        passwordPlaceholder={this.props.passwordPlaceholder}
      />
    );
  }

  getPromoCodeFormField() {
    let promoCodeRow = null;
    if (this.props.promoCode) {
      const enablePromoCode = this.isFieldEnabled('promoCode');
      const promoCodeFormClasses = classNames(
        'control-group',
        {
          disabled: !enablePromoCode || this.props.isPromoCodeHidden,
          hidden: this.props.isPromoCodeHidden,
        },
      );
      const promoCodeValidationProps = {
        type: 'text',
        name: 'promo-code',
        id: 'promoCode',
        className: 'input-xlarge',
        value: this.props.promoCode,
        disabled: !enablePromoCode,
        validations: [],
      };
      promoCodeRow = (
        <BaseFormRow
          formClasses={promoCodeFormClasses}
          inputClasses="controls"
          htmlFor="promoCode"
          label="Promo Code:"
          stateKey="promoCode"
          validationProps={promoCodeValidationProps}
        />
      );
    }
    return promoCodeRow;
  }

  getEmailConsentCheckbox() {
    const marketingConsentCopy = `I would ${
      this.state.defaultMarketingConsent === undefined ? 'not' : ''
    } like to receive email marketing updates from FormSwift.`;

    if (this.state.isEmailMarketingConsentOptionShown) {
      return (
        <div className="email-marketing-consent-checkbox-wrapper">
          <input
            id="email-marketing-consent"
            name="email-marketing-consent"
            onChange={this.onMarketingConsentChange}
            type="checkbox"
          />
          <label
            htmlFor="email-marketing-consent"
          >
            {marketingConsentCopy}
          </label>
        </div>
      );
    }
    return null;
  }

  isFormValid() {
    return Object.keys(this.form.state.byId).reduce((validFields, id) => (
      validFields && !this.form.state.byId[id].error
    ), true);
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.form.validateAll();
    const validForm = this.isFormValid();
    if (validForm) {
      this.setState({ hasSubmittedRegisterRequest: true });
      window._track('Make Account', 'Click');
      // Submit the form
      if (this.props.shareRegister) {
        const payload = {
          ...FormSwiftAPI.buildPayload(e.target),
          share_key: this.props.shareKey,
          finish: true,
          email_marketing_consent: this.state.isConsentToEmailMarketingGiven,
        };
        const handleResult = () => {
          window.location.assign('/my-documents');
        };
        return window.FormSwiftAPI.handleShareRegister(
          e.target,
          payload['promo-code'],
          true,
          handleResult,
          payload,
        );
      }

      return window.FormSwiftAPI.handleInlineRegister(e.target, e);
    }

    return false;
  }

  isFieldEnabled(field) {
    let enableField = true;
    let fieldsRequiredBefore = [];
    switch (field) {
      case 'password':
        fieldsRequiredBefore = this.props['fields-before-password'];
        break;
      case 'promoCode':
        fieldsRequiredBefore = this.props['fields-before-promoCode'];
        break;
      default:
        break;
    }

    for (let i = 0; i < fieldsRequiredBefore.length; i += 1) {
      const validFieldRequiredBefore = this.state[`valid-${fieldsRequiredBefore[i]}`];
      enableField = enableField && validFieldRequiredBefore;
    }
    return enableField;
  }

  validateField(fieldName) {
    this.form.validate(fieldName);
  }

  render() {
    const emailRow = this.getEmailFormField();
    const passwordRow = this.getPasswordFormField();
    const confirmPassword = this.getConfirmPasswordFormField();
    const promoCodeRow = this.getPromoCodeFormField();
    const hiddenFormFields = this.getHiddenFormFields();
    const emailConsentCheckbox = this.getEmailConsentCheckbox();

    return (
      <Form
        id="chargifyForm"
        method="post"
        target="_parent"
        onSubmit={this.handleSubmit}
        ref={(c) => { this.form = c; }}
        className="new-account-form inlineAccountCreation"
      >
        <h1 className="new-account-page-header">{this.props.heading}</h1>
        <fieldset>
          {emailRow}
          {passwordRow}
          {confirmPassword}
          {promoCodeRow}
          {this.props.displayTOS === RegisterFormTOSPositionEnum.TOP &&
            <div className="register-with-tos">
              <NewAccountTermsOfService />
            </div>
          }
          {emailConsentCheckbox}
          <div className="form-actions submit-button-wrapper">
            {hiddenFormFields}
            <button
              id="submitRegister"
              type="submit"
              disabled={this.state.hasSubmittedRegisterRequest}
              className="btn btn-primary btn-large btn-success submit-button"
              data-loading-text="Creating Account..."
            >
             Create Account
            </button>
          </div>
        </fieldset>
        {this.props.displayTOS === RegisterFormTOSPositionEnum.BOTTOM &&
          <div className="terms-of-service">
            <p className="terms-of-service-text">
              Your e-mail will not be shared with anyone.
              We will not send you junk mail or newsletters.
            </p>
            <NewAccountTermsOfService />
          </div>
        }
      </Form>
    );
  }
}

RegisterForm.defaultProps = {
  heading: 'Create An Account To Save Your Document',
  promoCode: '',
  emailPlaceholder: '',
  passwordPlaceholder: '',
  'fields-before-password': [],
  'fields-before-promoCode': [],
  funnelCategory: '',
  funnelPage: '',
  documentType: '',
  isPromoCodeHidden: '',
  sessionKey: '',
  sessionHash: '',
  redirectToPage: '',
  originalPage: '',
  newsletter: '',
  hideConfirmPassword: true,
  shareKey: '',
  shareRegister: false,
  displayTOS: RegisterFormTOSPositionEnum.BOTTOM,
};

RegisterForm.propTypes = {
  heading: PropTypes.string,
  promoCode: PropTypes.string,
  emailPlaceholder: PropTypes.string,
  passwordPlaceholder: PropTypes.string,
  'fields-before-password': PropTypes.arrayOf(
    PropTypes.oneOf(['email']),
  ),
  'fields-before-promoCode': PropTypes.arrayOf(
    PropTypes.oneOf(['email', 'password']),
  ),
  funnelCategory: PropTypes.string,
  funnelPage: PropTypes.string,
  documentType: PropTypes.string,
  isPromoCodeHidden: PropTypes.bool,
  sessionKey: PropTypes.string,
  sessionHash: PropTypes.string,
  redirectToPage: PropTypes.string,
  originalPage: PropTypes.string,
  newsletter: PropTypes.string,
  hideConfirmPassword: PropTypes.bool,
  shareKey: PropTypes.string,
  shareRegister: PropTypes.bool,
  displayTOS: PropTypes.oneOf([
    RegisterFormTOSPositionEnum.TOP,
    RegisterFormTOSPositionEnum.BOTTOM,
    RegisterFormTOSPositionEnum.NONE,
  ]),
};

export default RegisterForm;



// WEBPACK FOOTER //
// ./src-react/RegisterForm/index.jsx